import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { portfolioService } from '../../services/portfolio.service';
import { requestCache } from '../../utils/requestCache';

// Types
export interface PortfolioItem {
  id: string;
  userId: string;
  imageUrl: string;
  thumbnailUrl?: string;
  title?: string;
  caption?: string;
  category?: string;
  tags?: string[];
  views: number;
  likes: number;
  createdAt: string;
}

export interface PortfolioPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PortfolioState {
  items: PortfolioItem[];
  pagination: PortfolioPagination | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

// Initial state
const initialState: PortfolioState = {
  items: [],
  pagination: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

// Async thunks
export const fetchPortfolio = createAsyncThunk(
  'portfolio/fetch',
  async (params: { page?: number; limit?: number; category?: string } = {}, { rejectWithValue }) => {
    try {
      const cacheKey = `fetchPortfolio_${JSON.stringify(params)}`;
      const response = await requestCache.get(cacheKey, () =>
        portfolioService.getPortfolio(params)
      );
      if (response.success && response.data) {
        return response.data;
      }
      return rejectWithValue('Failed to fetch portfolio');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch portfolio');
    }
  }
);


export const deletePortfolioItem = createAsyncThunk(
  'portfolio/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await portfolioService.deletePortfolioItem(id);
      if (response.success) {
        return id;
      }
      return rejectWithValue('Failed to delete portfolio item');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete portfolio item');
    }
  }
);

// Slice
const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    clearPortfolio: (state) => {
      state.items = [];
      state.pagination = null;
      state.error = null;
      state.lastFetched = null;
    },
    addPortfolioItem: (state, action: PayloadAction<PortfolioItem>) => {
      state.items.unshift(action.payload);
    },
    removePortfolioItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    updatePortfolioItem: (state, action: PayloadAction<PortfolioItem>) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch portfolio
      .addCase(fetchPortfolio.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPortfolio.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.items || [];
        state.pagination = action.payload.pagination || null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchPortfolio.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete portfolio item
      .addCase(deletePortfolioItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePortfolioItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(deletePortfolioItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPortfolio, addPortfolioItem, removePortfolioItem, updatePortfolioItem } = portfolioSlice.actions;
export default portfolioSlice.reducer;
